import { DESTINY_PROTOCOL, DOWNLOAD_URL } from "@/shared/constants/app";
import { useProcessOtp } from "@/shared/hooks/useProfile";
import { UpdateIPCService } from "@/shared/services/UpdateIPCService";
import { AxiosError } from "axios";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./Verify.module.css";

export const VerifyPage: React.FC = observer(() => {
  const [searchParams] = useSearchParams();
  const otp = searchParams.get("otp")
  const navigate = useNavigate();
  const verifyMutation = useProcessOtp();

  useEffect(() => {
    if (otp) {
      
      verifyMutation.mutate({ otp: otp });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const getStatusMessage = () => {
    if (verifyMutation.isPending) {
      return {
        title: "Проверка email...",
        message: "Пожалуйста, подождите, мы проверяем ваш email адрес.",
        type: "loading" as const,
      };
    }

    if (verifyMutation.isSuccess) {
      return {
        title: "Email успешно подтвержден!",
        message:
          "Ваш email адрес был успешно подтвержден. Теперь вы можете войти в систему.",
        type: "success" as const,
      };
    }

    if (verifyMutation.error) {
      const error = verifyMutation.error as AxiosError;
      const errorStatus = error?.response?.status;

      switch (errorStatus) {
        case 400:
          return {
            title: "Неверная ссылка",
            message:
              "Ссылка для подтверждения email недействительна или истекла.",
            type: "error" as const,
          };
        case 404:
          return {
            title: "Ссылка не найдена",
            message: "Ссылка для подтверждения email не найдена.",
            type: "error" as const,
          };
        case 409:
          return {
            title: "Email уже подтвержден",
            message: "Этот email адрес уже был подтвержден ранее.",
            type: "warning" as const,
          };
        default:
          return {
            title: "Ошибка подтверждения",
            message: `Произошла ошибка при подтверждении email: ${errorStatus || "неизвестная ошибка"}`,
            type: "error" as const,
          };
      }
    }

    return {
      title: "Ошибка",
      message: "Не удалось обработать запрос на подтверждение email.",
      type: "error" as const,
    };
  };

  const status = getStatusMessage();

  const getStatusIcon = () => {
    switch (status.type) {
      case "loading":
        return (
          <div className={styles.iconLoading} />
        );
      case "success":
        return (
          <div className={styles.iconSuccess}>
            <svg
              className={styles.iconSvg}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        );
      case "warning":
        return (
          <div className={styles.iconWarning}>
            <svg
              className={styles.iconSvg}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
        );
      case "error":
        return (
          <div className={styles.iconError}>
            <svg
              className={styles.iconSvg}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        );
    }
  };

  const getCardClassName = () => {
    switch (status.type) {
      case "loading":
        return `${styles.card} ${styles.cardLoading}`;
      case "success":
        return `${styles.card} ${styles.cardSuccess}`;
      case "warning":
        return `${styles.card} ${styles.cardWarning}`;
      case "error":
        return `${styles.card} ${styles.cardError}`;
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.background}>
        <div className={styles.backgroundBlur1} />
        <div className={styles.backgroundBlur2} />
        <div className={styles.backgroundBlur3} />
      </div>

      <div className={styles.content}>
        <div className={getCardClassName()}>
          <div className={styles.iconWrap}>{getStatusIcon()}</div>

          <div className={styles.titleBlock}>
            <h1 className={styles.title}>{status.title}</h1>
            <p className={styles.message}>{status.message}</p>
          </div>

          <div className={styles.buttons}>
            {status.type === "success" && (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className={styles.buttonPrimary}
                >
                  Войти в систему
                </button>
                {!UpdateIPCService.isAvailable() && (
                  <div className={styles.appBlock}>
                    <p className={styles.appBlockText}>
                      Откройте приложение Destiny Games для входа
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        window.location.href = DESTINY_PROTOCOL;
                      }}
                      className={styles.buttonApp}
                    >
                      Открыть Destiny Games
                    </button>
                    <a
                      href={DOWNLOAD_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.downloadLink}
                    >
                      Скачать приложение
                    </a>
                  </div>
                )}
              </>
            )}

            {(status.type === "error" || status.type === "warning") && (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className={styles.buttonRegister}
                >
                  Зарегистрироваться заново
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className={styles.buttonSecondary}
                >
                  Перейти к входу
                </button>
              </>
            )}

            <button
              type="button"
              onClick={() => navigate("/")}
              className={styles.buttonMain}
            >
              На главную
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
